/**
 * Standardized error handling utility.
 * Maps Firebase error codes to user-friendly messages and provides
 * a consistent error object format across the application.
 */

/**
 * Firebase Auth error code to user-friendly message mapping.
 */
const AUTH_ERROR_MESSAGES = {
  'auth/email-already-in-use': 'This email is already registered. Try logging in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
  'auth/weak-password': 'Password is too weak. Please use at least 8 characters with a mix of letters and numbers.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email. Please sign up first.',
  'auth/wrong-password': 'Incorrect password. Please try again or reset your password.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later or reset your password.',
  'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
  'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
  'auth/missing-password': 'Please enter your password.',
  'auth/invalid-login-credentials': 'Invalid email or password. Please check your credentials and try again.',
};

/**
 * Firebase Firestore error code to user-friendly message mapping.
 */
const FIRESTORE_ERROR_MESSAGES = {
  'permission-denied': 'You do not have permission to perform this action.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'resource-exhausted': 'Too many requests. Please try again later.',
  'failed-precondition': 'The operation could not be completed. Please try again.',
  'aborted': 'The operation was cancelled. Please try again.',
  'unavailable': 'The service is temporarily unavailable. Please try again later.',
  'deadline-exceeded': 'The request took too long. Please try again.',
  'unauthenticated': 'You must be logged in to perform this action.',
};

/**
 * Firebase Storage error code to user-friendly message mapping.
 */
const STORAGE_ERROR_MESSAGES = {
  'storage/unauthorized': 'You do not have permission to access this file.',
  'storage/canceled': 'The upload was cancelled.',
  'storage/unknown': 'An unknown error occurred during file upload.',
  'storage/object-not-found': 'The requested file was not found.',
  'storage/quota-exceeded': 'Storage quota exceeded. Please contact support.',
  'storage/retry-limit-exceeded': 'The upload failed after multiple attempts. Please try again.',
  'storage/invalid-checksum': 'The file was corrupted during upload. Please try again.',
};

/**
 * Get a user-friendly error message for a Firebase error code.
 * @param {string} errorCode - The Firebase error code.
 * @returns {string} A human-readable error message.
 */
export const getErrorMessage = (errorCode) => {
  if (!errorCode) return 'An unexpected error occurred. Please try again.';

  return (
    AUTH_ERROR_MESSAGES[errorCode] ||
    FIRESTORE_ERROR_MESSAGES[errorCode] ||
    STORAGE_ERROR_MESSAGES[errorCode] ||
    'An unexpected error occurred. Please try again.'
  );
};

/**
 * Create a standardized error object from a Firebase error.
 * @param {Error} error - The original error.
 * @param {string} [fallbackMessage] - Optional fallback message if the error code is not recognized.
 * @returns {{ code: string, message: string, originalError: Error }}
 */
export const createAppError = (error, fallbackMessage) => {
  const code = error?.code || 'unknown';
  const message = fallbackMessage || getErrorMessage(error?.code) || error?.message || 'An unexpected error occurred.';

  return {
    code,
    message,
    originalError: error,
  };
};

/**
 * Handle a service-level error by logging (in development) and throwing a standardized error.
 * @param {Error} error - The original error.
 * @param {string} context - A description of what operation was being performed.
 * @param {string} [fallbackMessage] - Optional user-friendly fallback message.
 * @throws {{ code: string, message: string, originalError: Error }}
 */
export const handleServiceError = (error, context, fallbackMessage) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, error);
  }

  const appError = createAppError(error, fallbackMessage);
  throw appError;
};
